import React from "react";
import { useAlarm } from "./hooks/useAlarm";
import { useForceUpdate } from "./hooks/useForceUpdate";
import { useWindowInnerHeight } from "./hooks/useWindowInnerHeight";
import { Button } from "./Button";
import { Clock } from "./Clock";
import { DigitalTimer } from "./DigitalTimer";
import { ReactComponent as LoadingIcon } from "./assets/loading.svg";
import "./App.css";

const SECOND_IN_MILLISECONDS = 1000;
const TICK = SECOND_IN_MILLISECONDS / 2;

enum Mode {
  RUNNING = "RUNNING",
  PAUSED = "PAUSED",
  STOPPED = "STOPPED",
  FINISHED = "FINISHED",
}

enum ActionType {
  CHANGE_DURATION = "CHANGE_DURATION",
  RESET = "RESET",
  PAUSE = "PAUSE",
  RESUME = "RESUME",
  START = "START",
  FINISH = "FINISH",
}

interface IState {
  startTimestamp: number | null;
  initialDuration: number;
  duration: number;
  mode: Mode;
}

type ActionChangeDurationT = {
  type: ActionType.CHANGE_DURATION;
  duration: number;
};

type ActionResetT = {
  type: ActionType.RESET;
};

type ActionStartT = {
  type: ActionType.START;
};

type ActionResumeT = {
  type: ActionType.RESUME;
};

type ActionPauseT = {
  type: ActionType.PAUSE;
};

type ActionFinishedT = {
  type: ActionType.FINISH;
};

type ActionT = ActionChangeDurationT | ActionResetT | ActionStartT | ActionResumeT | ActionPauseT | ActionFinishedT;

function reducer(state: IState, action: ActionT): IState {
  switch (action.type) {
    case ActionType.CHANGE_DURATION: {
      return { ...state, duration: action.duration };
    }
    case ActionType.RESET: {
      return { ...state, mode: Mode.STOPPED, duration: state.initialDuration, startTimestamp: null };
    }
    case ActionType.START: {
      return {
        ...state,
        startTimestamp: Date.now(),
        mode: Mode.RUNNING,
        initialDuration: state.duration,
      };
    }
    case ActionType.PAUSE: {
      if (state.startTimestamp == null) {
        throw new TypeError(`'startTimestamp' cannot be null in ${ActionType.PAUSE} action`);
      }
      const timestamp = Date.now();
      return {
        ...state,
        mode: Mode.PAUSED,
        startTimestamp: timestamp,
        duration: state.duration - (timestamp - state.startTimestamp),
      };
    }
    case ActionType.RESUME: {
      return {
        ...state,
        mode: Mode.RUNNING,
        startTimestamp: Date.now(),
      };
    }
    case ActionType.FINISH: {
      return {
        ...state,
        mode: Mode.FINISHED,
        startTimestamp: null,
      };
    }
  }
}

function getDuration({
  mode,
  startTimestamp,
  duration,
}: {
  mode: Mode;
  startTimestamp: number | null;
  duration: number;
}) {
  if (mode === Mode.RUNNING && startTimestamp != null) {
    return duration - (Date.now() - startTimestamp);
  }
  return mode === Mode.FINISHED ? 0 : duration;
}

export function App() {
  const [state, dispatch] = React.useReducer<React.Reducer<IState, ActionT>>(reducer, {
    startTimestamp: null,
    initialDuration: 0,
    duration: 0,
    mode: Mode.STOPPED,
  });

  const windowInnerHeight = useWindowInnerHeight();
  const forceUpdate = useForceUpdate();
  const { isAlarmLoading, isAlarmPlaying, setIsAlarmPlaying } = useAlarm();
  const { startTimestamp, mode, initialDuration } = state;

  const duration = getDuration({ mode, startTimestamp, duration: state.duration });

  const handleChangeDuration = React.useCallback((duration: number) => {
    dispatch({ type: ActionType.CHANGE_DURATION, duration });
  }, []);

  function handleReset() {
    setIsAlarmPlaying(false);
    dispatch({ type: ActionType.RESET });
  }
  function handleStart() {
    dispatch({ type: ActionType.START });
  }

  function handleResume() {
    dispatch({ type: ActionType.RESUME });
  }

  function handlePause() {
    dispatch({ type: ActionType.PAUSE });
  }

  function handleFinish() {
    setIsAlarmPlaying(true);
    dispatch({ type: ActionType.FINISH });
  }

  React.useEffect(() => {
    if (mode !== Mode.RUNNING) {
      return () => {};
    }
    const intervalId = setInterval(() => {
      if (mode !== Mode.RUNNING) {
        return;
      }
      forceUpdate();
    }, TICK);

    return () => {
      clearInterval(intervalId);
    };
  }, [forceUpdate, mode]);

  React.useEffect(() => {
    if (duration > 0 || mode !== Mode.RUNNING) {
      return;
    }
    handleFinish();
  });

  React.useEffect(() => {
    if (mode === Mode.FINISHED && !isAlarmPlaying) {
      dispatch({ type: ActionType.RESET });
    }
  }, [isAlarmPlaying, mode]);

  return (
    <div className="App" style={{ height: windowInnerHeight }}>
      {isAlarmLoading ? (
        <div className="App__loading">
          <LoadingIcon />
        </div>
      ) : (
        <div className="App__content">
          <Clock editable={mode === Mode.STOPPED} duration={duration} onChangeDuration={handleChangeDuration} />
          <DigitalTimer editable={mode === Mode.STOPPED} duration={duration} onChangeDuration={handleChangeDuration} />
          <div className="App__buttons">
            <Button disabled={mode === Mode.STOPPED && duration === initialDuration} onClick={handleReset}>
              Reset
            </Button>
            {mode === Mode.PAUSED ? (
              <Button disabled={duration === 0} onClick={handleResume}>
                Resume
              </Button>
            ) : null}
            {mode === Mode.RUNNING ? (
              <Button disabled={duration === 0} onClick={handlePause}>
                Pause
              </Button>
            ) : null}
            {mode === Mode.STOPPED ? (
              <Button disabled={duration === 0} onClick={handleStart}>
                Start
              </Button>
            ) : null}
            {mode === Mode.FINISHED ? <Button onClick={handleReset}>Ok</Button> : null}
          </div>
        </div>
      )}
    </div>
  );
}
